// Turning Claude's raw replies into answers and grades. Pure functions, so they can be tested without a browser.
import type { Confidence, GradedItem, Question, RawCheckReply, RawReadReply, ReadItem, Submission, Verdict, Worksheet } from "../shared/types.js";

export interface Extraction { printedTitle: string; items: ReadItem[] }

export const str = (v: unknown): string => (v == null ? "" : String(v));
export const num = (v: unknown, d = 0): number => { const n = Number(v); return Number.isFinite(n) ? n : d; };
export const asRecord = (v: unknown): Record<string, unknown> => (v && typeof v === "object" ? (v as Record<string, unknown>) : {});
export const CONFIDENCES: readonly Confidence[] = ["high", "medium", "low"];
export const VERDICT_SET: readonly Verdict[] = ["correct", "partial", "incorrect", "blank", "unclear"];
const isConfidence = (v: unknown): v is Confidence => CONFIDENCES.includes(v as Confidence);
const isVerdict = (v: unknown): v is Verdict => VERDICT_SET.includes(v as Verdict);
export const strList = (v: unknown, max: number): string[] => (Array.isArray(v) ? v.map(String).slice(0, max) : []);

export function normalizeExtraction(r: RawReadReply): Extraction {
  const items = Array.isArray(r.items) ? r.items : [];
  return {
    printedTitle: r.printed_title ? str(r.printed_title) : "",
    items: items.map((raw: unknown, i): ReadItem => {
      const it = asRecord(raw);
      return {
        label: str(it.label ?? i + 1) || String(i + 1), prompt: str(it.prompt), response: str(it.response), work: str(it.work),
        confidence: isConfidence(it.confidence) ? it.confidence : "medium",
        flags: strList(it.flags, 5),
      };
    }),
  };
}
export function mergeCheck(items: ReadItem[], r: RawCheckReply, ws: Worksheet | null): GradedItem[] {
  const fb = (Array.isArray(r.items) ? r.items : []).map(asRecord);
  const key: Question[] = ws?.questions || [];
  return items.map((it, i): GradedItem => {
    const f = fb.find(x => str(x.label) === it.label) || fb[i] || {};
    const k = key.find(q => str(q.label) === it.label) || (key.length === items.length ? key[i] : null);
    const possible = k ? num(k.points, 1) : num(f.points_possible, 1) || 1;
    const verdict: Verdict = isVerdict(f.verdict) ? f.verdict : "unclear";
    const awarded = Math.max(0, Math.min(possible, num(f.points_awarded, verdict === "correct" ? possible : 0)));
    return { ...it, expected: str(f.expected), verdict, points_awarded: awarded, points_possible: possible, note: str(f.note) };
  });
}
export function recompute(sub: Submission): void {
  sub.score = sub.items.reduce((a, it) => a + num(it.points_awarded), 0);
  sub.possible = sub.items.reduce((a, it) => a + num(it.points_possible), 0);
}
export function csvCell(v: unknown): string { const s = str(v); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; }
